import clsx from "clsx";

import type { NotificationKind } from "../statusPresentation";

import {
  BaseAlertTriangleIcon,
  ChevronUpDoubleIcon,
  ClockIcon,
  CrossIcon,
  GiftIcon,
  LockUnlockedIcon,
  ShieldIcon,
} from "../../Icons";
import { Typo } from "../../Typo";

type WidgetNotificationProps = {
  kind: NotificationKind;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  dismissLabel?: string;
  roundedBottom?: boolean;
};

export type WidgetNotificationEntry = Omit<
  WidgetNotificationProps,
  "roundedBottom"
>;

const KIND_STYLES: Record<
  NotificationKind,
  {
    surface: string;
    textColor: string;
    iconColor: string;
    Icon: typeof ClockIcon;
    role: "status";
  }
> = {
  event: {
    surface: "bg-surface-brand-subtle",
    textColor: "text-text-informative-bold",
    iconColor: "text-icon-brand-default",
    Icon: GiftIcon,
    role: "status",
  },
  completeProfile: {
    surface: "bg-surface-brand-subtle",
    textColor: "text-text-informative-bold",
    iconColor: "text-icon-brand-default",
    Icon: LockUnlockedIcon,
    role: "status",
  },
  grace: {
    surface: "bg-surface-accent-violet-subtler",
    textColor: "text-text-accent-violet-bold",
    iconColor: "[&_path]:stroke-icon-accent-violet-bold",
    Icon: ClockIcon,
    role: "status",
  },
  tierUpgrade: {
    surface: "bg-surface-accent-green-subtler",
    textColor: "text-text-accent-green-bold",
    iconColor: "[&_path]:stroke-text-accent-green-bold",
    Icon: ChevronUpDoubleIcon,
    role: "status",
  },
  tierDowngrade: {
    surface: "bg-surface-accent-amber-subtler",
    textColor: "text-text-accent-amber-bold",
    iconColor: "rotate-180 [&_path]:stroke-icon-warning-bold",
    Icon: ChevronUpDoubleIcon,
    role: "status",
  },
  tierFreeze: {
    surface: "bg-surface-brand-subtle",
    textColor: "text-text-informative-bold",
    iconColor: "[&_path]:stroke-icon-brand-default",
    Icon: ShieldIcon,
    role: "status",
  },
  tierUnfreeze: {
    surface: "bg-surface-brand-subtle",
    textColor: "text-text-informative-bold",
    iconColor: "[&_path]:stroke-icon-brand-default",
    Icon: ShieldIcon,
    role: "status",
  },
  qualificationCountdown: {
    surface: "bg-surface-accent-amber-subtler",
    textColor: "text-text-accent-amber-bold",
    iconColor: "text-icon-warning-bold",
    Icon: BaseAlertTriangleIcon,
    role: "status",
  },
};

export const WidgetNotification = ({
  kind,
  message,
  actionLabel,
  onAction,
  onDismiss,
  dismissLabel,
  roundedBottom = true,
}: WidgetNotificationProps) => {
  const { surface, textColor, iconColor, Icon, role } = KIND_STYLES[kind];

  return (
    <div
      role={role}
      className={clsx(
        "flex items-center gap-4 p-4",
        surface,
        roundedBottom && "rounded-b-2xl",
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <span className={clsx("flex shrink-0 items-start py-0.5", iconColor)}>
          <Icon height={16} width={16} aria-hidden="true" />
        </span>
        <Typo element="p" lgType="bodySM" smType="bodyXS" textColor={textColor}>
          {message}
        </Typo>
      </div>

      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused"
        >
          <Typo
            element="span"
            smType="labelXS"
            lgType="labelSM"
            textColor="text-text-link-default"
            className="underline"
          >
            {actionLabel}
          </Typo>
        </button>
      )}

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="shrink-0 rounded p-0.5 text-icon-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused"
        >
          <CrossIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
